import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { getApiEndpoint } from "../../config/config";
import { handleGenericError } from "../../utils/errorHandler";
import {
  Card,
  Text,
  Group,
  Stack,
  Loader,
  Alert,
  Title,
  SimpleGrid,
  Badge,
  Button,
} from "@mantine/core";
import {
  IconAlertCircle,
  IconRefresh,
  IconUser,
  IconUserStar,
  IconEdit,
} from "@tabler/icons-react";
import PageContent from "../shared/PageContent/PageContent";
import EditTeamRepsModal from "./EditTeamRepsModal";

function TeamDetailsPage() {
  const { id } = useParams();
  const { getVerifiedJWT } = useAuth();
  const [team, setTeam] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editRepsOpen, setEditRepsOpen] = useState(false);

  const fetchTeam = async () => {
    try {
      setLoading(true);
      const jwt = await getVerifiedJWT();

      if (!jwt) {
        setError("Failed to fetch team");
        return;
      }

      const response = await fetch(getApiEndpoint(`/v1/teams/${id}`), {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
      });

      if (response.ok) {
        const result = await response.json();
        setTeam(result.data ?? result);
        setError(null);
      } else {
        const errorData = await response.json().catch(() => ({}));
        setError(errorData.message || "Failed to fetch team");
      }
    } catch (err) {
      handleGenericError(err, "Failed to fetch team");
      setError("Failed to fetch team");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTeam();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const formatDate = dateString => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch {
      return "Invalid Date";
    }
  };

  const renderPerson = person => {
    const fullName =
      person.name ||
      [person.first_name, person.last_name].filter(Boolean).join(" ") ||
      person.email ||
      "Unnamed";
    return (
      <Card key={person.id} padding="sm" radius="md" withBorder>
        <Stack gap={4}>
          <Text fw={600}>{fullName}</Text>
          {person.email && (
            <Text size="sm" c="dimmed">
              {person.email}
            </Text>
          )}
        </Stack>
      </Card>
    );
  };

  return (
    <PageContent
      title={team?.name || "Team"}
      breadcrumbs={[
        { label: "Selling Years", path: "/selling-years" },
        { label: team?.name || "Team" },
      ]}
    >
      <Stack gap="lg">
        {loading && (
          <Group justify="center" py="xl">
            <Loader size="md" />
            <Text>Loading team...</Text>
          </Group>
        )}

        {error && !loading && (
          <Alert icon={<IconAlertCircle size={16} />} title="Error" color="red">
            <Group justify="space-between">
              <Text>{error}</Text>
              <Button
                variant="outline"
                size="sm"
                leftSection={<IconRefresh size={16} />}
                onClick={fetchTeam}
              >
                Retry
              </Button>
            </Group>
          </Alert>
        )}

        {!loading && !error && team && (
          <>
            <Card padding="lg" radius="md" withBorder>
              <Stack gap="sm">
                <Group justify="space-between" align="center">
                  <Title order={3}>{team.name}</Title>
                  {team.selling_year_name && (
                    <Badge variant="light">{team.selling_year_name}</Badge>
                  )}
                </Group>
                <Group gap="xl">
                  <Group gap="xs">
                    <Text size="sm" c="dimmed">
                      Reps:
                    </Text>
                    <Text size="sm" fw={600}>
                      {team.rep_count ?? 0}
                    </Text>
                  </Group>
                  <Group gap="xs">
                    <Text size="sm" c="dimmed">
                      Managers:
                    </Text>
                    <Text size="sm" fw={600}>
                      {team.manager_count ?? 0}
                    </Text>
                  </Group>
                  {team.created_at && (
                    <Group gap="xs">
                      <Text size="sm" c="dimmed">
                        Created:
                      </Text>
                      <Text size="sm">{formatDate(team.created_at)}</Text>
                    </Group>
                  )}
                </Group>
              </Stack>
            </Card>

            <Card padding="lg" radius="md" withBorder>
              <Group gap="xs" mb="sm">
                <IconUserStar size={18} />
                <Title order={5}>Managers</Title>
              </Group>
              {!team.managers || team.managers.length === 0 ? (
                <Text c="dimmed">No managers on this team.</Text>
              ) : (
                <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="md">
                  {team.managers.map(renderPerson)}
                </SimpleGrid>
              )}
            </Card>

            <Card padding="lg" radius="md" withBorder>
              <Group justify="space-between" align="center" mb="sm">
                <Group gap="xs">
                  <IconUser size={18} />
                  <Title order={5}>Reps</Title>
                </Group>
                <Button
                  size="xs"
                  leftSection={<IconEdit size={14} />}
                  onClick={() => setEditRepsOpen(true)}
                >
                  Edit Reps
                </Button>
              </Group>
              {!team.reps || team.reps.length === 0 ? (
                <Text c="dimmed">No reps on this team.</Text>
              ) : (
                <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="md">
                  {team.reps.map(renderPerson)}
                </SimpleGrid>
              )}
            </Card>
          </>
        )}
      </Stack>

      <EditTeamRepsModal
        isOpen={editRepsOpen}
        onClose={() => setEditRepsOpen(false)}
        team={team}
        onMembersUpdated={fetchTeam}
      />
    </PageContent>
  );
}

export default TeamDetailsPage;
